// Email Notifications Service using Google Apps Script
// Sends automated emails for appointments

export interface EmailConfig {
  webAppUrl: string
  enabled: boolean
  senderName: string
  senderEmail: string
  logoUrl?: string
}

export interface AppointmentEmailData {
  patientName: string
  patientEmail: string
  doctorName: string
  serviceName: string
  date: string
  time: string
  duration: number
  notes?: string
  cancelReason?: string
  oldDate?: string
  oldTime?: string
}

class EmailNotificationService {
  private config: EmailConfig | null = null

  constructor() {
    this.loadConfig()
  }

  private loadConfig() {
    const saved = localStorage.getItem('savidna-email-config')
    if (saved) {
      try {
        this.config = JSON.parse(saved)
      } catch (e) {
        console.error('Error loading email config:', e)
      }
    }
  }

  saveConfig(config: EmailConfig) {
    this.config = config
    localStorage.setItem('savidna-email-config', JSON.stringify(config))
  }

  getConfig(): EmailConfig | null {
    return this.config
  }

  isEnabled(): boolean {
    return this.config?.enabled && !!this.config?.webAppUrl
  }

  async sendAppointmentCreated(
    data: AppointmentEmailData,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return { success: false, error: 'Email notifications not configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'appointment_created',
          senderName: this.config.senderName,
          senderEmail: this.config.senderEmail,
          logoUrl: this.config.logoUrl || '',
          data,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error sending appointment created email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async sendAppointmentUpdated(
    data: AppointmentEmailData,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return { success: false, error: 'Email notifications not configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'appointment_updated',
          senderName: this.config.senderName,
          senderEmail: this.config.senderEmail,
          logoUrl: this.config.logoUrl || '',
          data,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error sending appointment updated email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async sendAppointmentCancelled(
    data: AppointmentEmailData,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return { success: false, error: 'Email notifications not configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'appointment_cancelled',
          senderName: this.config.senderName,
          senderEmail: this.config.senderEmail,
          logoUrl: this.config.logoUrl || '',
          data,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error sending appointment cancelled email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async sendAppointmentRescheduled(
    data: AppointmentEmailData,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return { success: false, error: 'Email notifications not configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'appointment_rescheduled',
          senderName: this.config.senderName,
          senderEmail: this.config.senderEmail,
          logoUrl: this.config.logoUrl || '',
          data,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error sending appointment rescheduled email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async sendAppointmentReminder(
    data: AppointmentEmailData,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return { success: false, error: 'Email notifications not configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'appointment_reminder',
          senderName: this.config.senderName,
          senderEmail: this.config.senderEmail,
          logoUrl: this.config.logoUrl || '',
          data,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error sending appointment reminder email:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    if (!this.config?.webAppUrl) {
      return { success: false, error: 'No Web App URL configured' }
    }

    try {
      await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'test',
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error testing email connection:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      }
    }
  }

  getSetupInstructions(): string {
    return 'Ver instrucciones en la interfaz de configuración'
  }
}

export const emailNotificationService = new EmailNotificationService()
